import * as Sentry from '@sentry/react';
import i18n from 'i18next';

// Read by public/index.html before loading GTM and Kameleoon, keep both in sync
const SAFE_MODE_KEY = '_ifcp_safe_mode_until';
const SAFE_MODE_DURATION_MS = 24 * 60 * 60 * 1000;

const TICK_MS = 1000;
const WINDOW_MS = 10000;
const BLOCKED_RATIO_TO_TRIGGER = 0.7;
// Larger gaps come from device sleep or background throttling, not from a busy page
const SUSPEND_GAP_MS = 30000;

type Sample = { at: number; blocked: number };

export function isSafeMode(): boolean {
    try {
        return Number(window.localStorage.getItem(SAFE_MODE_KEY)) > Date.now();
    } catch {
        return false;
    }
}

function enableSafeMode() {
    try {
        window.localStorage.setItem(SAFE_MODE_KEY, String(Date.now() + SAFE_MODE_DURATION_MS));
    } catch {
        // Storage can be unavailable in private mode
    }
}

function scriptKey(sourceURL: string): string {
    if (!sourceURL) {
        return 'inline';
    }
    try {
        const url = new URL(sourceURL);
        return url.origin + url.pathname;
    } catch {
        return sourceURL;
    }
}

// Long Animation Frames (Chrome 123+) report which scripts kept the main thread busy
function observeScriptTime(): Record<string, number> {
    const totals: Record<string, number> = {};
    try {
        if (!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) {
            return totals;
        }
        const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry: any) => {
                (entry.scripts || []).forEach((script: any) => {
                    const key = scriptKey(script.sourceURL);
                    totals[key] = (totals[key] || 0) + script.duration;
                });
            });
        });
        observer.observe({ type: 'long-animation-frame', buffered: true });
    } catch {
        // Unsupported browsers only lose the script attribution
    }
    return totals;
}

function topScripts(totals: Record<string, number>) {
    return Object.keys(totals)
        .sort((a, b) => totals[b] - totals[a])
        .slice(0, 5)
        .map((key) => ({ script: key, ms: Math.round(totals[key]) }));
}

// Plain DOM so it can still be shown while React is starved of main-thread time
function showFreezeBanner() {
    const banner = document.createElement('div');
    banner.setAttribute('role', 'alert');
    banner.style.cssText =
        'position:fixed;left:16px;right:16px;bottom:16px;z-index:2147483647;display:flex;gap:12px;align-items:center;justify-content:space-between;' +
        'padding:12px 16px;border-radius:8px;background:#212121;color:#fff;font:14px/1.4 sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.3);';

    const message = document.createElement('span');
    message.textContent = i18n.t('common.page_unresponsive', {
        defaultValue: 'The page is not responding. Reload to continue.'
    });

    const reload = document.createElement('button');
    reload.type = 'button';
    reload.textContent = i18n.t('common.reload', { defaultValue: 'Reload' });
    reload.style.cssText =
        'flex-shrink:0;padding:8px 16px;border:0;border-radius:4px;background:#fff;color:#212121;font-weight:600;cursor:pointer;';
    reload.onclick = () => window.location.reload();

    banner.appendChild(message);
    banner.appendChild(reload);
    document.body.appendChild(banner);
}

export function startFreezeGuard() {
    const scriptTotals = observeScriptTime();
    let samples: Sample[] = [];
    let last = performance.now();
    let triggered = false;

    const reset = () => {
        samples = [];
        last = performance.now();
    };
    document.addEventListener('visibilitychange', reset);

    const interval = window.setInterval(() => {
        const now = performance.now();
        const elapsed = now - last;
        last = now;
        if (document.hidden || elapsed > SUSPEND_GAP_MS) {
            samples = [];
            return;
        }
        samples.push({ at: now, blocked: Math.max(0, elapsed - TICK_MS) });
        samples = samples.filter((sample) => now - sample.at <= WINDOW_MS);
        const blocked = samples.reduce((total, sample) => total + sample.blocked, 0);
        if (triggered || blocked / WINDOW_MS < BLOCKED_RATIO_TO_TRIGGER) {
            return;
        }

        triggered = true;
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', reset);

        const wasSafeMode = isSafeMode();
        enableSafeMode();
        showFreezeBanner();
        Sentry.captureMessage('UI freeze detected', {
            level: 'error',
            tags: { ui_freeze: 'true', safe_mode: String(wasSafeMode) },
            extra: { blocked_ms: Math.round(blocked), window_ms: WINDOW_MS, top_scripts: topScripts(scriptTotals) },
            fingerprint: ['ui-freeze', String(wasSafeMode)]
        });
    }, TICK_MS);
}
